import type { Config } from "@codeshare/config";
import {
  authProof,
  deriveRoomKeys,
  fingerprintFromSdp,
  newNonce,
  type RoomKeys,
  verifyAuthProof,
} from "@codeshare/crypto";
import { bindIroh, type IrohBinder } from "@codeshare/iroh";
import {
  type AppInfo,
  CHANNELS,
  type ChannelName,
  type ControlMessage,
  channelProtocol,
  decodeControl,
  encodeControl,
  type Hello,
  negotiate,
  PROTOCOLS,
  parseChannelProtocol,
  randomId,
  SCHEMAS,
  type VersionRange,
} from "@codeshare/protocol";
import type { SignalingAdapter } from "@codeshare/signaling";
import {
  type Channel,
  type ChannelOptions,
  type ConnectionStats,
  type MeshPeer,
  PeerMesh,
  type PeerOptions,
} from "@codeshare/transport";
import { createAdapters } from "./adapters";

export interface Profile {
  /** Stable per browser. */
  clientId: string;
  name: string;
  /** Catppuccin accent. */
  color: string;
}

export type RemotePeerState = "connecting" | "authenticating" | "ready";

/** Direct (WebRTC, possibly through TURN), or through an iroh relay after that failed. */
export type PeerTransport = "webrtc" | "iroh";

export interface RemotePeer {
  id: string;
  state: RemotePeerState;
  transport: PeerTransport;
  profile?: Profile;
  app?: AppInfo;
  caps: string[];
  /** Agreed version per feature channel; null = no common version (feature off for this peer). */
  channels: Partial<Record<ChannelName, number | null>>;
  schemas: Partial<Record<string, number | null>>;
  /** Round-trip time, ms. */
  rtt?: number;
}

/** What the connection diagnostics panel shows for one peer (no addresses, no secrets). */
export interface PeerDiagnostics {
  id: string;
  name?: string;
  state: RemotePeerState;
  app?: { version: string; sha: string };
  /** Control-channel ping round trip, ms. */
  rtt?: number;
  negotiated: Partial<Record<ChannelName, number | null>>;
  connection: Omit<ConnectionStats, "channels"> & {
    channels: (ConnectionStats["channels"][number] & { bufferedAmount: number })[];
  };
}

/** Feature channels opened automatically once a peer is ready. `term` opens per run session. */
export const AUTO_CHANNELS: Partial<Record<ChannelName, ChannelOptions>> = {
  doc: { ordered: true },
  aware: { ordered: false, maxRetransmits: 0 },
  chat: { ordered: true },
  blob: { ordered: true },
};

export interface RoomOptions {
  secret: string;
  config: Config;
  profile: Profile;
  app: AppInfo;
  caps?: string[];
  /** Overrides for tests and embedders. */
  adapters?: SignalingAdapter[];
  /** How relayed (iroh) endpoints are made; tests use an in-memory network. */
  iroh?: { bind: IrohBinder };
  protocols?: Record<string, VersionRange>;
  helloTimeoutMs?: number;
  pingIntervalMs?: number;
  /** Most other people to connect to (the mesh's default, 8, unless set). */
  maxPeers?: number;
  peerOptions?: Partial<
    Pick<
      PeerOptions,
      "gatherTimeoutMs" | "restartDelayMs" | "connectTimeoutMs" | "RTCPeerConnection"
    >
  >;
  /** Test hooks: simulate other builds and misbehaving peers. */
  testing?: {
    keys?: (real: RoomKeys) => RoomKeys;
    transformHello?: (hello: Hello) => object;
    suppressHello?: boolean;
  };
}

interface PeerSession {
  peer: MeshPeer;
  remote: RemotePeer;
  nonce: string;
  verified: boolean;
  acknowledged: boolean;
  channels: Map<string, Channel>;
  /** Channels this side opened with openChannel (not announced to its own listeners). */
  ownChannels: WeakSet<Channel>;
  pendingChannels: Channel[];
  timers: ReturnType<typeof setTimeout>[];
}

type ChannelListener = (peerId: string, channel: Channel, version: number) => void;

/**
 * A joined room: the peer mesh plus, per peer, the hello/auth handshake, version negotiation,
 * feature channel setup, and liveness pings (docs/PRD.md §5–7).
 */
export class Room {
  readonly selfId = randomId();
  #options: RoomOptions & { helloTimeoutMs: number; pingIntervalMs: number };
  readonly #sessions = new Map<string, PeerSession>();
  readonly #peerListeners = new Set<(peers: RemotePeer[]) => void>();
  readonly #channelListeners = new Map<string, Set<ChannelListener>>();
  readonly #fullListeners = new Set<(maxPeers: number) => void>();
  #mesh: PeerMesh | undefined;
  #keys: RoomKeys | undefined;
  #protocols: Record<string, VersionRange>;

  constructor(options: RoomOptions) {
    this.#options = { helloTimeoutMs: 10_000, pingIntervalMs: 5_000, ...options };
    this.#protocols = options.protocols ?? { ...PROTOCOLS };
  }

  get topic(): string | undefined {
    return this.#keys?.topic;
  }

  /** Snapshot of everyone we're connected (or connecting) to. */
  /**
   * The people in the room. Someone shows once they've answered (they're really there): an
   * announcement alone may be from a tab that has since closed.
   */
  peers(): RemotePeer[] {
    return [...this.#sessions.values()]
      .filter((s) => s.peer.answered || s.remote.state !== "connecting")
      .map((s) => ({ ...s.remote }));
  }

  /** Someone in the room turned us away: they're connected to as many people as allowed. */
  onFull(listener: (maxPeers: number) => void): () => void {
    this.#fullListeners.add(listener);
    return () => this.#fullListeners.delete(listener);
  }

  /** People this device couldn't connect to, repeatedly (gone, or unreachable without TURN). */
  get unreachable(): number {
    return this.#mesh?.unreachable.length ?? 0;
  }

  onPeersChange(listener: (peers: RemotePeer[]) => void): () => void {
    this.#peerListeners.add(listener);
    return () => this.#peerListeners.delete(listener);
  }

  /** A feature channel opened with a peer (name without version, e.g. "doc"). */
  onChannel(name: string, listener: ChannelListener): () => void {
    const listeners = this.#channelListeners.get(name) ?? new Set();
    this.#channelListeners.set(name, listeners);
    listeners.add(listener);
    // Replay channels that are already open (including labelled ones, "term:<id>"), so late
    // subscribers don't miss peers. Channels this side opened itself aren't incoming: skipped.
    for (const [peerId, session] of this.#sessions) {
      const version = session.remote.channels[name as ChannelName];
      if (!version) continue;
      for (const [label, channel] of session.channels) {
        if (label.split(":")[0] === name && !session.ownChannels.has(channel)) {
          listener(peerId, channel, version);
        }
      }
    }
    return () => listeners.delete(listener);
  }

  /**
   * Opens an extra channel of a feature with a ready peer, labelled `<feature>:<suffix>` (e.g.
   * "term:<session id>"). The peer gets it through `onChannel(feature)`; this side's listeners
   * don't (it isn't incoming). Undefined if the peer isn't ready or doesn't speak the feature.
   */
  openChannel(peerId: string, label: string, options: ChannelOptions = { ordered: true }) {
    const session = this.#sessions.get(peerId);
    if (!session) return undefined;
    const name = label.split(":")[0] ?? "";
    const version = session.remote.channels[name as ChannelName];
    if (session.remote.state !== "ready" || !version) return undefined;
    const channel = session.peer.openChannel(label, channelProtocol(name, version), options);
    session.ownChannels.add(channel);
    this.#acceptChannel(session, channel, false);
    return session.channels.get(label) === channel ? channel : undefined;
  }

  /** Connection details for every peer (selected path, RTT, per-channel traffic and backlog). */
  async diagnostics(): Promise<PeerDiagnostics[]> {
    return Promise.all(
      [...this.#sessions.values()].map(async (session) => {
        let stats: ConnectionStats = { channels: [] };
        try {
          stats = await session.peer.stats();
        } catch {}
        const buffered = (label: string) =>
          label === session.peer.control.label
            ? session.peer.control.bufferedAmount
            : (session.channels.get(label)?.bufferedAmount ?? 0);
        const { remote } = session;
        return {
          id: remote.id,
          ...(remote.profile ? { name: remote.profile.name } : {}),
          state: remote.state,
          ...(remote.app ? { app: { version: remote.app.version, sha: remote.app.sha } } : {}),
          ...(remote.rtt !== undefined ? { rtt: remote.rtt } : {}),
          negotiated: { ...remote.channels },
          connection: {
            ...stats,
            channels: stats.channels.map((c) => ({ ...c, bufferedAmount: buffered(c.label) })),
          },
        };
      }),
    );
  }

  channel(peerId: string, name: string): Channel | undefined {
    return this.#sessions.get(peerId)?.channels.get(name);
  }

  async join(): Promise<void> {
    if (this.#mesh) return;
    const real = await deriveRoomKeys(this.#options.secret);
    this.#keys = this.#options.testing?.keys?.(real) ?? real;
    const config = this.#options.config;
    this.#mesh = new PeerMesh({
      selfId: this.selfId,
      topic: this.#keys.topic,
      sigKey: this.#keys.sigKey,
      adapters: this.#options.adapters ?? createAdapters(config.signaling),
      rtcConfig: { iceServers: config.iceServers, iceTransportPolicy: config.iceTransportPolicy },
      ...(config.irohRelays.length > 0
        ? { iroh: { relayUrls: config.irohRelays, bind: this.#options.iroh?.bind ?? bindIroh } }
        : {}),
      ...(this.#options.peerOptions ? { peerOptions: this.#options.peerOptions } : {}),
      ...(this.#options.maxPeers ? { maxPeers: this.#options.maxPeers } : {}),
    });
    this.#mesh.onPeer((peer) => this.#attach(peer));
    this.#mesh.onPeerLeft((peerId) => this.#detach(peerId));
    this.#mesh.onUnreachableChange(() => this.#emitPeers());
    this.#mesh.onFull((_peerId, maxPeers) => {
      for (const listener of this.#fullListeners) listener(maxPeers);
    });
    await this.#mesh.start();
  }

  /** Updates our display name/color and tells every connected peer. */
  setProfile(profile: Pick<Profile, "name" | "color">): void {
    this.#options.profile = { ...this.#options.profile, ...profile };
    for (const session of this.#sessions.values()) {
      if (session.remote.state === "ready") {
        this.#send(session, { t: "profile", name: profile.name, color: profile.color });
      }
    }
  }

  async leave(): Promise<void> {
    for (const session of this.#sessions.values()) {
      void session.peer.control
        .send(encodeControl({ t: "bye", reason: "leaving" }))
        .catch(() => {});
    }
    for (const peerId of [...this.#sessions.keys()]) this.#detach(peerId);
    await this.#mesh?.stop();
    this.#mesh = undefined;
  }

  #attach(peer: MeshPeer): void {
    const session: PeerSession = {
      peer,
      remote: {
        id: peer.peerId,
        state: "connecting",
        transport: peer.kind,
        caps: [],
        channels: {},
        schemas: {},
      },
      nonce: newNonce(),
      verified: false,
      acknowledged: false,
      channels: new Map(),
      ownChannels: new WeakSet(),
      pendingChannels: [],
      timers: [],
    };
    this.#sessions.set(peer.peerId, session);
    this.#emitPeers();

    peer.onChannel((channel) => {
      if (session.verified && session.remote.state === "ready")
        this.#acceptChannel(session, channel);
      else session.pendingChannels.push(channel);
    });
    peer.control.onMessage((message) => {
      if (typeof message === "string") void this.#onControl(session, message);
    });
    peer.onAnswered(() => this.#emitPeers());
    peer.control.opened.then(
      () => {
        session.remote.state = "authenticating";
        this.#emitPeers();
        this.#sendHello(session);
        session.timers.push(
          setTimeout(() => {
            if (session.remote.state !== "ready") this.#reject(session, "handshake timeout");
          }, this.#options.helloTimeoutMs),
        );
      },
      () => {},
    );
  }

  #detach(peerId: string): void {
    const session = this.#sessions.get(peerId);
    if (!session) return;
    for (const timer of session.timers) clearTimeout(timer);
    for (const channel of session.channels.values()) channel.close();
    session.peer.close();
    this.#sessions.delete(peerId);
    this.#emitPeers();
  }

  #sendHello(session: PeerSession): void {
    if (this.#options.testing?.suppressHello) return;
    const { profile, app, caps = [] } = this.#options;
    const hello: Hello = {
      t: "hello",
      app,
      proto: this.#protocols,
      schema: { ...SCHEMAS },
      caps,
      peer: { id: this.selfId, ...profile },
      nonce: session.nonce,
    };
    const wire = this.#options.testing?.transformHello?.(hello) ?? hello;
    void session.peer.control.send(JSON.stringify(wire)).catch(() => {});
  }

  #send(session: PeerSession, message: ControlMessage): void {
    void session.peer.control.send(encodeControl(message)).catch(() => {});
  }

  async #onControl(session: PeerSession, raw: string): Promise<void> {
    const message = decodeControl(raw);
    switch (message.t) {
      case "hello":
        return this.#onHello(session, message);
      case "auth":
        return this.#onAuth(session, message.proof);
      case "auth.ok":
        session.acknowledged = true;
        return this.#maybeReady(session);
      case "ping":
        return this.#send(session, { t: "pong", ts: message.ts });
      case "pong":
        session.remote.rtt = Math.max(0, Date.now() - message.ts);
        return this.#emitPeers();
      case "profile":
        if (!session.verified || !session.remote.profile) return;
        session.remote.profile = {
          ...session.remote.profile,
          name: message.name.slice(0, 100),
          color: message.color.slice(0, 32),
        };
        return this.#emitPeers();
      case "bye":
        return this.#detach(session.peer.peerId);
      default:
        return; // unknown (newer peer) or invalid: ignore, per the compatibility rules
    }
  }

  async #onHello(session: PeerSession, hello: Hello): Promise<void> {
    if (session.remote.profile) return; // one hello per connection
    if (hello.peer.id !== session.peer.peerId) return this.#reject(session, "peer id mismatch");
    const { id: _id, ...profile } = hello.peer;
    session.remote.profile = profile;
    session.remote.app = hello.app;
    session.remote.caps = hello.caps;
    session.remote.channels = negotiate(
      this.#protocols as Record<ChannelName, VersionRange>,
      hello.proto,
    );
    session.remote.schemas = negotiate(SCHEMAS, hello.schema);
    this.#emitPeers();

    const fingerprints = this.#fingerprints(session);
    if (!fingerprints || !this.#keys) return this.#reject(session, "no fingerprints");
    const proof = await authProof(
      this.#keys.authKey,
      fingerprints.local,
      fingerprints.remote,
      hello.nonce,
    );
    this.#send(session, { t: "auth", proof });
  }

  async #onAuth(session: PeerSession, proof: string): Promise<void> {
    const fingerprints = this.#fingerprints(session);
    if (!fingerprints || !this.#keys) return this.#reject(session, "no fingerprints");
    const ok = await verifyAuthProof(
      this.#keys.authKey,
      proof,
      fingerprints.remote,
      fingerprints.local,
      session.nonce,
    );
    if (!ok) return this.#reject(session, "auth failed");
    session.verified = true;
    this.#send(session, { t: "auth.ok" });
    this.#maybeReady(session);
  }

  #maybeReady(session: PeerSession): void {
    if (!session.verified || !session.acknowledged || !session.remote.profile) return;
    if (session.remote.state === "ready") return;
    session.remote.state = "ready";
    this.#emitPeers();

    // The polite side opens feature channels, so each is created exactly once.
    if (session.peer.polite) {
      for (const [name, options] of Object.entries(AUTO_CHANNELS)) {
        const version = session.remote.channels[name as ChannelName];
        if (!version) continue;
        this.#acceptChannel(
          session,
          session.peer.openChannel(name, channelProtocol(name, version), options),
        );
      }
    }
    for (const channel of session.pendingChannels.splice(0)) this.#acceptChannel(session, channel);

    const ping = () => {
      if (!this.#sessions.has(session.peer.peerId)) return;
      this.#send(session, { t: "ping", ts: Date.now() });
      session.timers.push(setTimeout(ping, this.#options.pingIntervalMs));
    };
    ping();
  }

  /** Keeps a feature channel if its label and protocol match what we negotiated. */
  #acceptChannel(session: PeerSession, channel: Channel, notify = true): void {
    const parsed = parseChannelProtocol(channel.protocol ?? "");
    const label = channel.label ?? "";
    const name = label.split(":")[0] ?? "";
    const known = (CHANNELS as readonly string[]).includes(name);
    const version = session.remote.channels[name as ChannelName];
    if (!parsed || !known || parsed.name !== name || parsed.version !== version) {
      channel.close();
      return;
    }
    session.channels.set(label, channel);
    channel.onClose(() => {
      if (session.channels.get(label) === channel) session.channels.delete(label);
    });
    if (!notify) return;
    for (const listener of this.#channelListeners.get(name) ?? []) {
      listener(session.peer.peerId, channel, parsed.version);
    }
  }

  /**
   * What identifies this exact connection, for the auth proof: the DTLS certificate fingerprints
   * of a WebRTC connection, or the endpoint keys (which QUIC's TLS authenticates) of a relayed
   * one. Either way, a relay that swapped them couldn't produce a proof.
   */
  #fingerprints(session: PeerSession): { local: string; remote: string } | undefined {
    const { peer } = session;
    if (peer.kind === "iroh") {
      const local = peer.localEndpointId;
      const remote = peer.connected ? peer.remoteEndpointId : undefined;
      return local && remote ? { local: `iroh ${local}`, remote: `iroh ${remote}` } : undefined;
    }
    const local = fingerprintFromSdp(peer.localSdp ?? "");
    const remote = fingerprintFromSdp(peer.remoteSdp ?? "");
    return local && remote ? { local, remote } : undefined;
  }

  #reject(session: PeerSession, reason: string): void {
    this.#send(session, { t: "bye", reason });
    const peerId = session.peer.peerId;
    this.#detach(peerId);
    this.#mesh?.block(peerId); // don't let it reconnect and fail again in a loop
  }

  #emitPeers(): void {
    const snapshot = this.peers();
    for (const listener of this.#peerListeners) listener(snapshot);
  }
}
